import { useTranslation } from 'react-i18next';
import BlankPrimaryLayout from 'src/layouts/BlankPrimaryLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import LogoMonkey from 'src/images/logo-monkey.svg?react';
import { AutoFloatingPanel } from 'src/components/common/AutoFloatingPanel';
import FamilyCodePanel from 'src/components/Onboarding/FamilyCodePanel';
import LoginPanel from 'src/components/Login/LoginPanel';
import { useAuth } from 'src/hooks/useAuth';
import { AuthMode } from 'src/contexts/AuthContext';
import { UserType } from 'src/types/user.types';

// User type selection (parent or child) before signup
const SelectType = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setMode } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFamilyCodePanel, setShowFamilyCodePanel] = useState(false);
  const [showLoginPanel, setShowLoginPanel] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserType>();

  const onSelect = (role: UserType) => {
    // Save selected role and show family code panel
    setSelectedRole(role);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('user_type', role);
    setSearchParams(newParams);

    if (searchParams.get('family_code')) {
      navigate(`/select-family-user?${newParams.toString()}`);
      return;
    }

    setShowFamilyCodePanel(true);
  };

  // Handle navigation when user doesn't have a family code
  const handleContinueToSignup = () => {
    setShowFamilyCodePanel(false);

    if (!selectedRole) return;

    // Set mode to signup
    setMode(AuthMode.SIGNUP);

    // Show login panel
    setShowLoginPanel(true);
  };

  // After social login, handle redirection based on user type
  const handleAfterSocialLogin = () => {
    setShowLoginPanel(false);

    // After signup, navigate to appropriate onboarding step with params
    if (selectedRole === 'parent') {
      navigate(`/onboarding/parent/firstname?${searchParams.toString()}`, {
        replace: true,
      });
    } else {
      navigate(`/onboarding/child/firstname?${searchParams.toString()}`, {
        replace: true,
      });
    }
  };

  // Handle email signup button
  const handleEmailClick = () => {
    setShowLoginPanel(false);
    navigate(`/login/email?${searchParams.toString()}`);
  };

  return (
    <BlankPrimaryLayout>
      <div
        className={
          'flex flex-row justify-between items-center text-white p-4 mt-20'
        }
      >
        <LogoMonkey className={'w-auto h-20'} />
      </div>
      <div
        className={
          'flex flex-col justify-between flex-1 items-center px-6 py-10 text-white'
        }
      >
        <div className={'font-bold text-xl text-center text-balance'}>
          {t('onboarding.who.title')}
        </div>
        <div className={'flex flex-row gap-8'}>
          <RoleItem
            emoji={'🧑'}
            title={t('onboarding.who.parent')}
            onClick={() => {
              onSelect(UserType.parent);
            }}
            data-testid={'role-parent'}
          />
          <RoleItem
            emoji={'🧒'}
            title={t('onboarding.who.child')}
            onClick={() => {
              onSelect(UserType.child);
            }}
            data-testid={'role-child'}
          />
        </div>
        <div />
      </div>

      {/* Family Code Panel */}
      <AutoFloatingPanel
        isOpen={showFamilyCodePanel}
        onClose={() => {
          setShowFamilyCodePanel(false);
        }}
        data-testid="family-code-panel"
      >
        <FamilyCodePanel
          selectedUserType={selectedRole}
          onClose={handleContinueToSignup}
        />
      </AutoFloatingPanel>

      {/* Login Panel */}
      <AutoFloatingPanel
        isOpen={showLoginPanel}
        onClose={() => {
          setShowLoginPanel(false);
        }}
        data-testid="login-panel"
      >
        <LoginPanel
          title={t('login.createAccount')}
          className="p-2"
          onLogin={handleAfterSocialLogin}
          onEmailClick={handleEmailClick}
        />
      </AutoFloatingPanel>
    </BlankPrimaryLayout>
  );
};

const RoleItem = ({
  title,
  emoji,
  onClick,
  'data-testid': dataTestId,
}: {
  title: string;
  emoji: string;
  onClick: () => void;
  'data-testid'?: string;
}) => {
  return (
    <div
      className={'flex flex-col justify-center items-center gap-2'}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <div
        className={
          'bg-white bg-opacity-25 rounded-full p-6 text-4xl leading-none'
        }
      >
        {emoji}
      </div>
      <div className={'text-base font-bold'}>{title}</div>
    </div>
  );
};

export default SelectType;
